import { useRouter } from 'next/router';
import { signIn, SignInAuthorizationParams } from 'next-auth/react';
import { useCallback } from 'react';

import { isSafeRedirect } from '../utils/navigator';
import { useFeatureFlag } from './useFeatureFlag';
import { useMetrics } from './useMetrics';

type SignInProps = {
  provider?: string;
  callbackUrl?: string;
  authParams?: SignInAuthorizationParams;
};

// Turn a caller-supplied destination into a safe relative path for `/login?redirect=`.
// Callers pass either an already-relative path, or an absolute URL built from
// `window.location.href`/`.origin` — normalize the latter down to path+search+hash so
// it survives `isSafeRedirect`'s open-redirect guard. Cross-origin absolute URLs (which
// shouldn't occur from same-app callers) are dropped rather than forwarded.
export const toRelativeRedirect = (destination?: string): string | undefined => {
  if (!destination) return undefined;
  if (isSafeRedirect(destination)) return destination;
  try {
    const url = new URL(destination);
    if (typeof window !== 'undefined' && url.origin !== window.location.origin) {
      return undefined;
    }
    const relative = `${url.pathname}${url.search}${url.hash}`;
    return isSafeRedirect(relative) ? relative : undefined;
  } catch {
    return undefined;
  }
};

function useSignIn() {
  const metrics = useMetrics();
  const router = useRouter();
  // When the new auth experience is on, send logged-out users to the in-app
  // /login screen instead of the legacy Azure B2C hosted flow. This is the shared
  // sign-in hook consumed by nav/layout "Log in" buttons and auth-guard prompts
  // across the app, so fixing it here migrates every caller at once.
  const newAuth = useFeatureFlag('US_AUTH');

  const signin = useCallback(
    async (props?: SignInProps) => {
      // Keep this in mixpanel due to funnel building
      metrics.logEvent(
        'Login',
        {
          sourcePage: window.location.pathname,
          provider: props?.provider ?? 'azure-ad-b2c',
        },
        true,
      );

      if (newAuth) {
        const dest =
          toRelativeRedirect(props?.callbackUrl) ??
          toRelativeRedirect(window.location.href) ??
          router.asPath ??
          '/';
        router.push(`/login?redirect=${encodeURIComponent(dest)}`);
        return;
      }

      const currentUrl = window.location.href;
      if (props) {
        await signIn(
          props.provider ? props.provider : 'azure-ad-b2c',
          {
            callbackUrl: props.callbackUrl
              ? props.callbackUrl
              : currentUrl
              ? currentUrl
              : '/',
          },
          props.authParams,
        );
      } else {
        await signIn('azure-ad-b2c', {
          callbackUrl: currentUrl ? currentUrl : '/',
        });
      }
    },
    [metrics, newAuth, router],
  );

  return {
    signIn: signin,
  };
}

export default useSignIn;
